/**
 * Reading the `@standardize_response` envelope — `{ok, data, meta, errors}`.
 *
 * ⚠️ THE ENVELOPE IS NOT ALWAYS WHERE THE BACKEND PUT IT. Two shapes reach a caller, and
 * assuming either one alone produces a wrong answer on the other:
 *
 *  1. **Raw** — `{ message: {ok, data, meta, errors} }`. Frappe returns every whitelisted
 *     method's payload under `message`.
 *  2. **Collapsed** — `{ message: <data> }`. The axios interceptor
 *     (httpClient.ts:263-295) rewrites the body when `message.ok` is a BOOLEAN:
 *       - `ok === false` → it REJECTS with an `ApiError` carrying the backend's message,
 *         so a refusal never arrives here as a resolved response at all;
 *       - `ok === true`  → it does `resp.data.message = env.data`, consuming the envelope
 *         and leaving the DATA at `message`, with `env.meta` stashed on `__meta`.
 *
 * So a caller that asserts `payload.ok === true` reads the collapsed DATA, finds no `ok`,
 * and throws on a response that actually succeeded — which is exactly what
 * `list_coupons` did on `{"message":{"ok":true,"data":{...}}}`.
 *
 * The discipline is unchanged: `ok` still decides, an absent or malformed envelope is
 * still a FAILURE, and success is never assumed optimistically. Only the LOCATION of the
 * envelope is negotiated here. This mirrors what `clinicalMasterDataApi.normalizeMethodResponse`
 * and `mortalityApi` already do — gate on the presence of `ok`, and treat its absence as
 * "the interceptor already vouched for this".
 */

export interface StandardEnvelope<T> {
  ok: boolean;
  data: T;
  meta: Record<string, unknown>;
  errors: unknown;
}

/**
 * Backend messages carry markup — `frappe.bold` wraps values in `<strong>`, e.g.
 * "Pet <strong>PET-00001</strong> is not on Pet Boarding <strong>BRD-00055</strong>."
 * Toasts render plain text, so the tags would show up literally.
 *
 * Line breaks are PRESERVED: a refusal that names several things puts each on its own line,
 * and flattening them into one paragraph is how a three-sentence instruction becomes a wall.
 */
export function stripEnvelopeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li)>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function isPlainObject(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Locate and read the envelope.
 *
 * Returns `null` when no envelope and no collapsed payload can be found — the caller must
 * treat that as a failure, never as an empty success.
 */
export function unwrapStandardEnvelope<T = unknown>(response: any): StandardEnvelope<T> | null {
  const body = response;

  if (!isPlainObject(body))
    return null;

  // Shape 1 — the envelope is still intact, somewhere.
  for (const candidate of [body.message, body.data, body]) {
    if (isPlainObject(candidate) && "ok" in candidate) {
      return {
        // Boolean(), not `=== true`: a backend that ever sends 1/0 still reads correctly.
        ok: Boolean(candidate.ok),
        // ⚠️ READ FROM `data`. Some endpoints ALSO mirror their payload keys to the top
        // level of the envelope beside `data`; `data` is the authoritative copy and the
        // mirrors are a convenience that may lag.
        data: (candidate.data ?? {}) as T,
        meta: isPlainObject(candidate.meta) ? candidate.meta : {},
        errors: candidate.errors,
      };
    }
  }

  // Shape 2 — the interceptor already collapsed it. It ONLY does that after seeing
  // `ok === true`, so the success has been verified upstream; `message` is now the data.
  if (body.message !== undefined) {
    return {
      ok: true,
      data: body.message as T,
      meta: isPlainObject(body.__meta) ? body.__meta : {},
      errors: undefined,
    };
  }

  // Neither shape. Nothing here says the call did what was asked.
  return null;
}

/**
 * The operator-facing reason out of an `errors` payload, whatever shape it takes.
 *
 * `errors[0].message` and NOTHING ELSE. It deliberately does NOT fall back to
 * `errors[0].details`: on the standardized endpoints `details` is a **full Python traceback
 * including server file paths** (docs/BOARDING_CREATE_ORDER_API.md §6). Putting that in front
 * of a desk user leaks server internals behind what looks like an error message. When
 * `message` is absent the right outcome is an empty string, so the caller falls back to its
 * own wording.
 *
 * `meta.code` is NOT a fallback either — "ValidationError" tells an operator nothing.
 */
export function readErrorsMessage(errors: unknown): string {
  if (typeof errors === "string")
    return stripEnvelopeHtml(errors);

  if (Array.isArray(errors)) {
    // Every entry, in order, deduplicated — a refusal that names three missing things must not
    // be trimmed to the first one. Each on its own line so the toast can lay them out.
    const lines: string[] = [];

    for (const entry of errors) {
      const text = typeof entry === "string"
        ? stripEnvelopeHtml(entry)
        : stripEnvelopeHtml((entry as any)?.message);

      if (text && !lines.includes(text))
        lines.push(text);
    }

    return lines.join("\n");
  }

  if (isPlainObject(errors))
    return stripEnvelopeHtml(errors.message);

  return "";
}

/**
 * The first human-readable reason out of an envelope.
 * The backend's own words are always preferable to anything written client-side.
 */
export function envelopeErrorMessage(envelope: StandardEnvelope<unknown> | null): string {
  return envelope ? readErrorsMessage(envelope.errors) : "";
}
